import React, { useEffect, useState } from "react";
import { Table, Input, Col, Row } from "antd";
import _ from "lodash";
import moment from "moment";

const ReceiversTable = ({ receivers }) => {
  const [data, setData] = useState(receivers);
  const [sortedInfo, setSortedInfo] = useState(null);
  const [search, setSearch] = useState({});
  const [showTable, setShowTable] = useState(true);
  const [req, setReq] = useState(false);

  const filterData = () => {
    let filtered = receivers;

    if (search?.name?.length > 0) {
      filtered = filtered.filter((entry) => {
        const fullName =
          entry?.name || `${entry?.first_name || ""} ${entry?.last_name || ""}`;
        return fullName.toLowerCase().includes(search?.name?.toLowerCase());
      });
    }

    if (search?.email?.length > 0) {
      filtered = filtered.filter((entry) =>
        entry?.email?.toLowerCase().includes(search?.email?.toLowerCase())
      );
    }

    if (search?.user_id?.length > 0) {
      filtered = filtered.filter((entry) =>
        entry?.user_id?.toLowerCase().includes(search?.user_id?.toLowerCase())
      );
    }

    if (search?.status?.length > 0) {
      filtered = filtered.filter((entry) =>
        (entry?.userCallHistory?.type || "—")
          .toLowerCase()
          .includes(search?.status?.toLowerCase())
      );
    }

    if (search?.accepted?.length > 0) {
      filtered = filtered.filter((entry) => {
        const formatted =
          entry?.userCallHistory?.callAcceptedAt &&
          moment
            .utc(entry.userCallHistory.callAcceptedAt)
            .local()
            .format("DD MMM YYYY hh:mm A");
        return formatted
          ?.toLowerCase()
          .includes(search?.accepted?.toLowerCase());
      });
    }

    if (search?.rejected?.length > 0) {
      filtered = filtered.filter((entry) => {
        const formatted =
          entry?.userCallHistory?.callRejectedAt &&
          moment
            .utc(entry.userCallHistory.callRejectedAt)
            .local()
            .format("DD MMM YYYY hh:mm A");
        return formatted
          ?.toLowerCase()
          .includes(search?.rejected?.toLowerCase());
      });
    }

    if (search?.callDuration?.length > 0) {
      filtered = filtered.filter((entry) => {
        const totalSeconds = Math.floor(
          entry?.userCallHistory?.callDuration || 0
        );
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const formatted = `${minutes}m ${seconds}s`;
        return formatted
          .toLowerCase()
          .includes(search?.callDuration?.toLowerCase());
      });
    }

    setData(filtered);
  };

  useEffect(() => {
    filterData();
  }, [req]);

  useEffect(() => {
    setData(receivers);
  }, [receivers]);

  useEffect(() => {
    debouncedCallApi();
  }, [sortedInfo]);

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 500)
  );

  const searchInput = (key) => (
    <Input
      placeholder=""
      onClick={(e) => e.stopPropagation()}
      value={search[key]}
      onChange={(e) => {
        setSearch({ ...search, [key]: e.target.value });
        debouncedCallApi();
      }}
    />
  );

  const columns = [
    {
      title: () => (
        <div>
          <div className="special-table-header-text">User ID</div>
          {searchInput("user_id")}
        </div>
      ),
      dataIndex: "user_id",
      key: "user_id",
      sorter: (a, b) =>
        (a?.user_id || "")
          .toLowerCase()
          .localeCompare((b?.user_id || "").toLowerCase()),
      sortOrder: sortedInfo?.columnKey === "user_id" && sortedInfo?.order,
      ellipsis: true,
      width: "12%",
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Name</div>
          {searchInput("name")}
        </div>
      ),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) =>
        (a?.name || `${a?.first_name || ""} ${a?.last_name || ""}`)
          .toLowerCase()
          .localeCompare(
            (
              b?.name || `${b?.first_name || ""} ${b?.last_name || ""}`
            ).toLowerCase()
          ),
      sortOrder: sortedInfo?.columnKey === "name" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (name, record) =>
        name || `${record?.first_name || ""} ${record?.last_name || ""}`,
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Email</div>
          {searchInput("email")}
        </div>
      ),
      dataIndex: "email",
      key: "email",
      sorter: (a, b) =>
        (a?.email || "")
          .toLowerCase()
          .localeCompare((b?.email || "").toLowerCase()),
      sortOrder: sortedInfo?.columnKey === "email" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Start Time</div>
          {searchInput("accepted")}
        </div>
      ),
      key: "accepted",
      sorter: (a, b) => {
        const aTime = a?.userCallHistory?.callAcceptedAt
          ? new Date(a.userCallHistory.callAcceptedAt).getTime()
          : 0;
        const bTime = b?.userCallHistory?.callAcceptedAt
          ? new Date(b.userCallHistory.callAcceptedAt).getTime()
          : 0;
        return aTime - bTime;
      },
      sortOrder: sortedInfo?.columnKey === "accepted" && sortedInfo?.order,
      render: (_, r) =>
        r.userCallHistory?.callAcceptedAt
          ? moment
              .utc(r.userCallHistory.callAcceptedAt)
              .local()
              .format("DD MMM YYYY hh:mm A")
          : "",
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">End Time</div>
          {searchInput("rejected")}
        </div>
      ),
      key: "rejected",
      sorter: (a, b) => {
        const aTime = a?.userCallHistory?.callRejectedAt
          ? new Date(a.userCallHistory.callRejectedAt).getTime()
          : 0;
        const bTime = b?.userCallHistory?.callRejectedAt
          ? new Date(b.userCallHistory.callRejectedAt).getTime()
          : 0;
        return aTime - bTime;
      },
      sortOrder: sortedInfo?.columnKey === "rejected" && sortedInfo?.order,
      render: (_, r) =>
        r.userCallHistory?.callRejectedAt
          ? moment
              .utc(r.userCallHistory.callRejectedAt)
              .local()
              .format("DD MMM YYYY hh:mm A")
          : "",
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Duration</div>
          {searchInput("callDuration")}
        </div>
      ),
      key: "callDuration",
      sorter: (a, b) =>
        (a?.userCallHistory?.callDuration || 0) -
        (b?.userCallHistory?.callDuration || 0),
      sortOrder: sortedInfo?.columnKey === "callDuration" && sortedInfo?.order,
      render: (_, r) => {
        if (!r.userCallHistory?.callDuration) return "";
        const totalSeconds = Math.floor(r.userCallHistory.callDuration);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}m ${seconds}s`;
      },
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Call Status</div>
          {searchInput("status")}
        </div>
      ),
      key: "status",
      sorter: (a, b) =>
        (a?.userCallHistory?.type || "").localeCompare(
          b?.userCallHistory?.type || ""
        ),
      sortOrder: sortedInfo?.columnKey === "status" && sortedInfo?.order,
      render: (_, r) =>
        r.userCallHistory?.type === "call_decline"
          ? "Declined"
          : r.userCallHistory?.type || "",
    },
  ];

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  return (
    <Row className="revisions-parent-row" style={{ minHeight: "20vh" }}>
      <Col span={24} className="heading-col">
        <Row align="middle">
          <Col>
            <div className="revisions-heading-div">
              <span className="heading-span">
                Receivers {data.length > 0 && `(${data.length})`}
              </span>
            </div>
          </Col>
        </Row>
      </Col>

      {showTable && (
        <Col span={24}>
          <Table
            className="special-table ims-doc-table users-manager-table"
            rowKey={(record) => record._id}
            dataSource={data}
            columns={columns}
            onChange={handleChange}
            pagination={false}
            expandable={{ showExpandColumn: false }}
            size="small"
          />
        </Col>
      )}
    </Row>
  );
};

export default ReceiversTable;
